// getIsCoveredByEntryGate.js
'use strict';
import { createSelector } from '../resource/createCachedSelector.js';

import { getIsInIframe } from '../resource/getJsEnvironment.js';
import getIsPreviewClear from '../selector/getIsPreviewClear.js';
import getIsRemoteConfigMerged from '../selector/getIsRemoteConfigMerged.js';
import getMeData from '../selector/getMeData.js';
import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  CONFIG_PRIORITY_CONFIGURE,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
} from '../resource/configPriority.js';
import {
  NEED_LANDING_COUNTRIES,
  NEED_OVER_18_COUNTRIES,
} from '../RemoteConfigKeys.js';

const isInIframe = getIsInIframe();

/**
 * Select whether an entry gate is taking over the screen.
 * Mirrors what Landing and AgeVerifierModal decide for themselves: the landing
 * page claims the countries it covers, the age verifier takes the over-18
 * countries left over. Sales popups and the login flow are out of scope, they
 * are opened by events rather than by remote config.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {boolean} Return true when an entry gate is on screen.
 */
const getIsCoveredByEntryGate = createSelector(
  state => {
    const priorityGroups = getOperationData(
      state,
      ['config'],
      'configurationGroups'
    );
    return getIsRemoteConfigMerged(
      state,
      priorityGroups?.[CONFIG_PRIORITY_PRESENCE_CLIENT]
        ? CONFIG_PRIORITY_PRESENCE_CLIENT
        : CONFIG_PRIORITY_CONFIGURE
    );
  },
  state => getMeData(state, 'country'),
  state => !!getMeData(state, 'token'),
  state => getRemoteConfigData(state, NEED_LANDING_COUNTRIES),
  state => getRemoteConfigData(state, NEED_OVER_18_COUNTRIES),
  getIsPreviewClear,
  (
    isRemoteConfigMerged,
    country,
    isAuthed,
    landingCountries,
    over18Countries,
    isPreviewClear
  ) => {
    if (isInIframe || isAuthed) {
      return false;
    }
    const isInLandingCountries = (landingCountries || []).includes(country);
    if (
      // Both gates wait for the merge before deciding, and persist can restore
      // the country ahead of the config, so neither signal is enough alone.
      !isRemoteConfigMerged ||
      !country ||
      // Landing only opens while age still needs verifying; utm clears it.
      (isInLandingCountries && !isPreviewClear) ||
      // The age verifier takes the over-18 countries landing did not claim.
      (!isInLandingCountries && (over18Countries || []).includes(country))
    ) {
      return true;
    }
    return false;
  }
);

export default getIsCoveredByEntryGate;
